import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { SafetyBoundaryNotice } from "@/components/safety/SafetyBoundaryNotice";
import { SafetyMargin } from "@/components/safety/SafetyMargin";
import { CriticalityClass } from "@/components/safety/CriticalityClass";
import { AnalogueMeter } from "@/components/data/AnalogueMeter";

export default function Safety() {
  const { data: safety, error: safetyError } = usePolling(() => api.get("/intelligence/safety").then((r) => r.data), 5000);
  const { data: thermal, error: thermalError } = usePolling(() => api.get("/intelligence/thermal").then((r) => r.data), 5000);

  if (safetyError || thermalError) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="safety-error">Could not load safety layer.</div>;
  if (!safety || !thermal) return <div className="text-sm text-[var(--oim-text-muted)]" data-testid="safety-loading">Loading…</div>;

  const routes = [["Benzene route (R-201)", thermal.benzene_route, "nitrator-r201"], ["Toluene route (R-202)", thermal.toluene_route, "nitrator-r202"]];

  return (
    <div className="space-y-8" data-testid="safety-page">
      <div>
        <h1 className="text-2xl font-bold font-heading text-[var(--oim-text)]">Safety & Thermal</h1>
        <SafetyBoundaryNotice />
      </div>

      <div className="space-y-3" data-testid="safety-margins-section">
        <div className="text-[10px] uppercase tracking-widest text-[var(--oim-text-dim)] font-heading">Safety Margins</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {safety.margins.map((m, i) => (
            <div key={i} className="rounded-xl border p-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
              <SafetyMargin paramId={`margin-${i}`} label={m.param} value={m.value} unit={m.unit} margin={m.value} lower={0} upper={150} state={m.state} />
            </div>
          ))}
        </div>
        <div className="text-[10px] text-[var(--oim-text-dim)]">not monetised</div>
      </div>

      <div className="space-y-3" data-testid="thermal-section">
        <div className="text-[10px] uppercase tracking-widest text-[var(--oim-text-dim)] font-heading">Thermal Safety / Criticality</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {routes.map(([label, r, id]) => (
            <div key={id} className="rounded-xl border p-4 space-y-4" style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}>
              <div className="text-sm font-heading font-bold text-[var(--oim-text)]">{label}</div>
              <div className="flex items-center gap-4">
                <AnalogueMeter
                  value={r.cooling_margin} min={0} max={130}
                  bands={[{ from: 0, to: 20, color: "#FF3B30" }, { from: 20, to: 60, color: "#FFB300" }, { from: 60, to: 130, color: "#00C853" }]}
                  label="Cooling margin" unit="K" sublabel={`MTSR ${r.mtsr}°C`} testId={`analogue-meter-cooling-margin-${id}`}
                />
                <div className="font-mono-data text-[10px] text-[var(--oim-text-dim)] space-y-1">
                  <div>MTT {r.mtt}°C</div>
                  <div>T_D24 {r.t_d24}°C</div>
                </div>
              </div>
              <CriticalityClass
                systemId={id} criticalityClass={r.criticality_class} distanceToNextClass={r.distance_to_next_class}
                mtt={r.mtt} tD24={r.t_d24} referenceValidated={r.reference_validated}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
